<script setup>
import {
  computed,
  reactive,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
  showAvatar: {
    type: Boolean,
    default: true,
  },
});

const store = useStore();

const ETAPAS = [
  {
    value: 'novo',
    label: 'Novo',
    dot: 'bg-n-slate-9',
    pill: 'bg-n-slate-3 text-n-slate-11',
  },
  {
    value: 'negociacao',
    label: 'Em negociação',
    dot: 'bg-n-blue-9',
    pill: 'bg-n-blue-3 text-n-blue-11',
  },
  {
    value: 'aguardando',
    label: 'Aguardando documento',
    dot: 'bg-n-amber-9',
    pill: 'bg-n-amber-3 text-n-amber-11',
  },
  {
    value: 'proposta',
    label: 'Proposta enviada',
    dot: 'bg-n-iris-9',
    pill: 'bg-n-iris-3 text-n-iris-11',
  },
  {
    value: 'fechado',
    label: 'Fechado',
    dot: 'bg-n-teal-9',
    pill: 'bg-n-teal-3 text-n-teal-11',
  },
  {
    value: 'sem_interesse',
    label: 'Sem interesse',
    dot: 'bg-n-ruby-9',
    pill: 'bg-n-ruby-3 text-n-ruby-11',
  },
];

const GROUPS = [
  {
    title: 'Dados',
    fields: [
      {
        key: 'cpf',
        label: 'CPF',
        kind: 'custom',
        placeholder: '000.000.000-00',
        copy: true,
      },
      {
        key: 'data_nascimento',
        label: 'Nascimento',
        kind: 'custom',
        placeholder: 'dd/mm/aaaa',
      },
      {
        key: 'phone_number',
        label: 'Telefone',
        kind: 'direct',
        placeholder: 'adicionar',
        copy: true,
      },
      {
        key: 'email',
        label: 'E-mail',
        kind: 'direct',
        placeholder: 'adicionar',
      },
    ],
  },
  {
    title: 'Convênio',
    fields: [
      {
        key: 'convenio',
        label: 'Convênio',
        kind: 'custom',
        placeholder: 'adicionar',
      },
      {
        key: 'matricula',
        label: 'Matrícula',
        kind: 'custom',
        placeholder: 'adicionar',
        copy: true,
      },
      {
        key: 'senha',
        label: 'Senha',
        kind: 'custom',
        placeholder: 'adicionar',
        copy: true,
      },
    ],
  },
];

const SIMULACAO = [
  { key: 'margem', label: 'Margem', kind: 'custom', prefix: 'R$', hi: true },
  { key: 'valor_parcela', label: 'Parcela', kind: 'custom', prefix: 'R$' },
  { key: 'prazo', label: 'Prazo', kind: 'custom' },
];

const SIMULACAO_TITLE = 'Simulação';
const OBSERVACAO_TITLE = 'Observação';
const OBSERVACAO_PLACEHOLDER = 'Anotar algo sobre o lead…';

const OBSERVACAO = { key: 'observacao', kind: 'custom' };
const NAME_FIELD = { key: 'name', kind: 'direct' };

const ALL_FIELDS = [
  ...GROUPS.flatMap(g => g.fields),
  ...SIMULACAO,
  OBSERVACAO,
  NAME_FIELD,
];

const draft = reactive({});
const focusedKey = ref(null);
const savedKey = ref(null);
const copiedKey = ref(null);
const etapaOpen = ref(false);
const etapaRef = ref(null);
let savedTimer = null;
let copiedTimer = null;

const customAttrs = computed(() => props.contact.custom_attributes || {});

const storedValue = field =>
  (field.kind === 'direct'
    ? props.contact[field.key]
    : customAttrs.value[field.key]) || '';

const syncDrafts = () => {
  ALL_FIELDS.forEach(f => {
    if (focusedKey.value === f.key) return;
    draft[f.key] = storedValue(f);
  });
};

watch(() => props.contact, syncDrafts, { immediate: true, deep: true });

const etapaAtual = computed(
  () => ETAPAS.find(e => e.value === customAttrs.value.etapa) || ETAPAS[0]
);

const flashSaved = key => {
  savedKey.value = key;
  clearTimeout(savedTimer);
  savedTimer = setTimeout(() => {
    if (savedKey.value === key) savedKey.value = null;
  }, 1600);
};

const persist = async (attrs, key) => {
  try {
    await store.dispatch('contacts/update', { id: props.contact.id, ...attrs });
    if (key) flashSaved(key);
  } catch (error) {
    if (key) draft[key] = storedValue(ALL_FIELDS.find(f => f.key === key));
    if (error instanceof DuplicateContactException) {
      const detail = error.contactErrorDetail;
      const invalid = Array.isArray(error.data) ? error.data : [];
      if (detail) useAlert(detail);
      else if (invalid.includes('email'))
        useAlert('Já existe um contato com esse e-mail');
      else if (invalid.includes('phone_number'))
        useAlert('Já existe um contato com esse telefone');
      else useAlert('Não foi possível salvar');
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(error.message || 'Não foi possível salvar');
    }
  }
};

const commit = async field => {
  if (focusedKey.value === field.key) focusedKey.value = null;
  const next = (draft[field.key] || '').trim();
  const prev = storedValue(field);
  if (next === prev) {
    draft[field.key] = prev;
    return;
  }
  if (field.key === 'name' && !next) {
    draft.name = prev;
    return;
  }
  draft[field.key] = next;
  if (field.kind === 'direct') {
    await persist({ [field.key]: next }, field.key);
  } else {
    await persist(
      { custom_attributes: { ...customAttrs.value, [field.key]: next } },
      field.key
    );
  }
};

const revert = (field, event) => {
  draft[field.key] = storedValue(field);
  focusedKey.value = null;
  event.target.blur();
};

const setEtapa = async etapa => {
  etapaOpen.value = false;
  if (etapa.value === customAttrs.value.etapa) return;
  await persist({
    custom_attributes: { ...customAttrs.value, etapa: etapa.value },
  });
};

const copyField = async field => {
  const value = storedValue(field);
  if (!value) return;
  try {
    await copyTextToClipboard(value);
    copiedKey.value = field.key;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => {
      if (copiedKey.value === field.key) copiedKey.value = null;
    }, 1500);
  } catch {
    useAlert('Não foi possível copiar');
  }
};

const onDocumentClick = event => {
  if (!etapaOpen.value) return;
  if (etapaRef.value && !etapaRef.value.contains(event.target)) {
    etapaOpen.value = false;
  }
};

const onDocumentKeydown = event => {
  if (!etapaOpen.value) return;
  if (event.key === 'Escape') {
    etapaOpen.value = false;
    return;
  }
  const index = Number(event.key) - 1;
  if (Number.isInteger(index) && ETAPAS[index]) {
    event.preventDefault();
    setEtapa(ETAPAS[index]);
  }
};

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onDocumentKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onDocumentKeydown);
  clearTimeout(savedTimer);
  clearTimeout(copiedTimer);
});

const fieldClass = field => {
  const base =
    'flex-1 min-w-0 h-[25px] rounded-md px-[7px] text-xs tabular-nums border outline-none bg-clip-padding placeholder:text-n-slate-9 focus:bg-n-solid-1 focus:border-n-blue-9 focus:ring-2 focus:ring-n-blue-4 text-n-slate-12';
  return draft[field.key]
    ? `${base} bg-transparent border-transparent group-hover:border-n-slate-6`
    : `${base} bg-n-slate-2 border-transparent group-hover:border-n-slate-6 group-hover:bg-n-solid-1`;
};

const simBoxClass = field => {
  const base =
    'flex items-center justify-center gap-1 h-[30px] rounded-md border px-1.5 focus-within:border-n-blue-9 focus-within:ring-2 focus-within:ring-n-blue-4 focus-within:bg-n-solid-1';
  if (!draft[field.key])
    return `${base} bg-n-slate-2 border-transparent hover:border-n-slate-6`;
  if (field.hi) return `${base} bg-n-teal-3 border-n-teal-6`;
  return `${base} bg-transparent border-n-slate-4 hover:border-n-slate-6`;
};

const simTextClass = field =>
  draft[field.key] && field.hi
    ? 'text-n-teal-11 font-semibold'
    : 'text-n-slate-12 font-medium';
</script>

<template>
  <div class="flex flex-col">
    <div class="flex flex-col gap-3 px-4 pt-3.5 pb-3 border-b border-n-weak">
      <div class="flex items-center gap-2.5">
        <Avatar
          v-if="showAvatar"
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="36"
          hide-offline-status
        />
        <div class="flex flex-col gap-px min-w-0 flex-1">
          <input
            id="contact-name"
            v-model="draft.name"
            class="w-full bg-transparent border border-transparent rounded px-1 -mx-1 text-[13px] font-semibold capitalize text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-blue-9 focus:ring-2 focus:ring-n-blue-4 focus:bg-n-solid-1"
            @focus="focusedKey = 'name'"
            @blur="commit(NAME_FIELD)"
            @keydown.enter.prevent="$event.target.blur()"
            @keydown.esc="revert(NAME_FIELD, $event)"
          />
          <button
            v-if="contact.phone_number"
            class="flex items-center gap-1.5 text-[11.5px] text-n-slate-10 tabular-nums hover:text-n-blue-11 w-fit"
            @click="copyField({ key: 'phone_number', kind: 'direct' })"
          >
            {{ contact.phone_number }}
            <span
              :class="
                copiedKey === 'phone_number'
                  ? 'i-lucide-check text-n-teal-11'
                  : 'i-lucide-copy opacity-50'
              "
              class="text-[11px]"
            />
          </button>
        </div>
      </div>

      <div ref="etapaRef" class="relative">
        <button
          class="w-full h-[33px] rounded-lg flex items-center gap-2 px-2.5"
          :class="etapaAtual.pill"
          @click="etapaOpen = !etapaOpen"
        >
          <span
            class="size-[7px] rounded-full shrink-0"
            :class="etapaAtual.dot"
          />
          <span class="flex-1 text-left text-[12.5px] font-medium">
            {{ etapaAtual.label }}
          </span>
          <span
            class="text-[13px] opacity-60"
            :class="etapaOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          />
        </button>

        <div
          v-if="etapaOpen"
          class="absolute left-0 right-0 top-full mt-1.5 z-30 p-1 rounded-[9px] bg-n-solid-1 border border-n-slate-4 shadow-lg flex flex-col gap-px"
        >
          <button
            v-for="(etapa, index) in ETAPAS"
            :key="etapa.value"
            class="h-[31px] rounded-md flex items-center gap-2.5 px-2.5 text-[12.5px]"
            :class="
              etapa.value === etapaAtual.value
                ? 'bg-n-blue-3 text-n-blue-11 font-medium'
                : 'text-n-slate-12 hover:bg-n-slate-3'
            "
            @click="setEtapa(etapa)"
          >
            <span class="size-[7px] rounded-full shrink-0" :class="etapa.dot" />
            <span class="flex-1 text-left">{{ etapa.label }}</span>
            <span class="text-[10px] text-n-slate-9 tabular-nums">
              {{ index + 1 }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4 px-4 pt-3.5 pb-1.5">
      <div
        v-for="group in GROUPS"
        :key="group.title"
        class="flex flex-col gap-0.5"
      >
        <div
          class="text-[10px] font-semibold uppercase tracking-[0.06em] text-n-slate-9 mb-1"
        >
          {{ group.title }}
        </div>
        <div
          v-for="field in group.fields"
          :key="field.key"
          class="group flex items-center gap-2 h-[30px] -mx-1.5 px-1.5 rounded-md hover:bg-n-slate-2"
        >
          <label
            :for="`contact-${field.key}`"
            class="w-[70px] shrink-0 text-[11.5px] text-n-slate-10"
          >
            {{ field.label }}
          </label>
          <input
            :id="`contact-${field.key}`"
            v-model="draft[field.key]"
            :placeholder="field.placeholder"
            :class="fieldClass(field)"
            @focus="focusedKey = field.key"
            @blur="commit(field)"
            @keydown.enter.prevent="$event.target.blur()"
            @keydown.esc="revert(field, $event)"
          />
          <div class="w-5 shrink-0 flex items-center justify-center">
            <span
              v-if="savedKey === field.key"
              class="i-lucide-check text-xs text-n-teal-11"
            />
            <button
              v-else-if="field.copy && draft[field.key]"
              class="text-n-slate-9 opacity-50 group-hover:opacity-100 group-hover:text-n-blue-11"
              @click="copyField(field)"
            >
              <span
                :class="
                  copiedKey === field.key
                    ? 'i-lucide-check text-n-teal-11'
                    : 'i-lucide-copy'
                "
                class="text-xs"
              />
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-0.5">
        <div
          class="text-[10px] font-semibold uppercase tracking-[0.06em] text-n-slate-9 mb-1"
        >
          {{ SIMULACAO_TITLE }}
        </div>
        <div class="grid grid-cols-3 gap-1.5">
          <div
            v-for="field in SIMULACAO"
            :key="field.key"
            class="flex flex-col gap-1"
          >
            <label
              :for="`contact-${field.key}`"
              class="text-[10px] text-n-slate-10 text-center"
            >
              {{ field.label }}
            </label>
            <div :class="simBoxClass(field)">
              <span
                v-if="field.prefix"
                class="text-[9.5px] shrink-0"
                :class="
                  draft[field.key] && field.hi
                    ? 'text-n-teal-11 opacity-70'
                    : 'text-n-slate-9'
                "
              >
                {{ field.prefix }}
              </span>
              <input
                :id="`contact-${field.key}`"
                v-model="draft[field.key]"
                placeholder="—"
                class="w-full min-w-0 bg-transparent border-0 outline-none text-center text-[13px] tabular-nums placeholder:text-n-slate-9 placeholder:font-normal"
                :class="simTextClass(field)"
                @focus="focusedKey = field.key"
                @blur="commit(field)"
                @keydown.enter.prevent="$event.target.blur()"
                @keydown.esc="revert(field, $event)"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-0.5">
        <div
          class="text-[10px] font-semibold uppercase tracking-[0.06em] text-n-slate-9 mb-1"
        >
          {{ OBSERVACAO_TITLE }}
        </div>
        <textarea
          id="contact-observacao"
          v-model="draft.observacao"
          rows="3"
          :placeholder="OBSERVACAO_PLACEHOLDER"
          class="w-full min-h-[54px] rounded-lg px-2.5 py-2 text-xs leading-relaxed resize-y border outline-none placeholder:text-n-slate-9 focus:bg-n-solid-1 focus:border-n-blue-9 focus:ring-2 focus:ring-n-blue-4"
          :class="
            draft.observacao
              ? 'bg-transparent border-n-slate-4 text-n-slate-11 hover:border-n-slate-6'
              : 'bg-n-slate-2 border-transparent text-n-slate-12 hover:border-n-slate-6 hover:bg-n-solid-1'
          "
          @focus="focusedKey = 'observacao'"
          @blur="commit(OBSERVACAO)"
          @keydown.esc="revert(OBSERVACAO, $event)"
        />
      </div>
    </div>
  </div>
</template>
